/**
 * Chords, stored as scale degrees.
 *
 * A chord in this app is never a letter. It is a degree of the scale (1-7), an
 * accidental against the key, a quality, and optionally a bass degree. Letters
 * are produced at render time by pointing that degree at a key.
 *
 * Two things fall out of this for free:
 *
 *   Transposing is not an operation. Rendering the same chords against a
 *   different key IS the transposition. Nothing is rewritten, so nothing can be
 *   missed halfway down the second verse.
 *
 *   The Numbers view is not a conversion. Nashville numbers are closer to the
 *   stored form than letters are — "1 4 5 6m" is almost literally what's in the
 *   database.
 *
 * Everything here is pure. No React, no Supabase.
 */

/* ------------------------------------------------------------------ keys */

/**
 * The seven note names of each major key, spelled correctly.
 *
 * Spelling is not cosmetic. The third of D is F#, never Gb — a chart that says
 * Gb sends a reader looking for the wrong shape, and in the numbers view the
 * spelling is what tells you whether you're on a 3 or a b4. A chromatic
 * lookup table can't do this; the key signature has to be written down.
 */
const KEY_NOTES = {
  C:  ['C', 'D', 'E', 'F', 'G', 'A', 'B'],
  G:  ['G', 'A', 'B', 'C', 'D', 'E', 'F#'],
  D:  ['D', 'E', 'F#', 'G', 'A', 'B', 'C#'],
  A:  ['A', 'B', 'C#', 'D', 'E', 'F#', 'G#'],
  E:  ['E', 'F#', 'G#', 'A', 'B', 'C#', 'D#'],
  B:  ['B', 'C#', 'D#', 'E', 'F#', 'G#', 'A#'],
  'F#': ['F#', 'G#', 'A#', 'B', 'C#', 'D#', 'E#'],
  F:  ['F', 'G', 'A', 'Bb', 'C', 'D', 'E'],
  Bb: ['Bb', 'C', 'D', 'Eb', 'F', 'G', 'A'],
  Eb: ['Eb', 'F', 'G', 'Ab', 'Bb', 'C', 'D'],
  Ab: ['Ab', 'Bb', 'C', 'Db', 'Eb', 'F', 'G'],
  Db: ['Db', 'Eb', 'F', 'Gb', 'Ab', 'Bb', 'C'],
  Gb: ['Gb', 'Ab', 'Bb', 'Cb', 'Db', 'Eb', 'F'],
}

/** Twelve keys in chromatic order — the steps a transpose control walks. */
export const KEY_WHEEL = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B']

export const isKey = (k) => Object.prototype.hasOwnProperty.call(KEY_NOTES, k)

/** Normalises what a writer might have typed into the key column. */
export function normalizeKey(raw) {
  if (!raw) return null
  const k = String(raw).trim().replace(/\s*(major|maj|M)$/i, '')
  const cap = k.charAt(0).toUpperCase() + k.slice(1).replace(/♯/g, '#').replace(/♭/g, 'b')
  if (isKey(cap)) return cap
  // C# and D# aren't in the wheel as written; their enharmonics are.
  const ENHARMONIC = { 'C#': 'Db', 'D#': 'Eb', 'G#': 'Ab', 'A#': 'Bb', Cb: 'B', Fb: 'E' }
  return ENHARMONIC[cap] ?? null
}

export function transposeKey(key, steps) {
  const i = KEY_WHEEL.indexOf(normalizeKey(key) ?? 'C')
  return KEY_WHEEL[(((i + steps) % 12) + 12) % 12]
}

/* -------------------------------------------------------------- qualities */

/**
 * `n` is what a numbers chart writes, `c` what a letter chart writes. They
 * differ only for the plain triads: a major chord is a bare "1" in Nashville
 * and a bare "D" in letters, and a minor is "6m" either way.
 *
 * `basic: true` are the five that cover most worship songs. The rest appear
 * only when Advanced is on — a writer putting a first chart together should not
 * have to scroll past m7b5 to reach a G.
 */
export const QUALITIES = [
  { id: 'maj',   n: '',      c: '',      label: 'major',        basic: true },
  { id: 'm',     n: 'm',     c: 'm',     label: 'minor',        basic: true },
  { id: 'sus4',  n: 'sus4',  c: 'sus4',  label: 'suspended 4th', basic: true },
  { id: '7',     n: '7',     c: '7',     label: 'dominant 7th', basic: true },
  { id: '5',     n: '5',     c: '5',     label: 'power chord',  basic: true },

  { id: 'sus2',  n: 'sus2',  c: 'sus2',  label: 'suspended 2nd' },
  { id: 'maj7',  n: 'maj7',  c: 'maj7',  label: 'major 7th' },
  { id: 'm7',    n: 'm7',    c: 'm7',    label: 'minor 7th' },
  { id: '6',     n: '6',     c: '6',     label: 'sixth' },
  { id: 'm6',    n: 'm6',    c: 'm6',    label: 'minor 6th' },
  { id: 'add9',  n: 'add9',  c: 'add9',  label: 'added 9th' },
  { id: '9',     n: '9',     c: '9',     label: 'ninth' },
  { id: 'maj9',  n: 'maj9',  c: 'maj9',  label: 'major 9th' },
  { id: 'm9',    n: 'm9',    c: 'm9',    label: 'minor 9th' },
  { id: '11',    n: '11',    c: '11',    label: 'eleventh' },
  { id: '7sus4', n: '7sus4', c: '7sus4', label: '7 suspended 4th' },
  { id: '13',    n: '13',    c: '13',    label: 'thirteenth' },
  { id: 'dim',   n: '°',     c: 'dim',   label: 'diminished' },
  { id: 'm7b5',  n: 'm7b5',  c: 'm7b5',  label: 'half-diminished' },
  { id: 'aug',   n: '+',     c: 'aug',   label: 'augmented' },
]

export const qualitiesFor = (advanced) =>
  advanced ? QUALITIES : QUALITIES.filter((q) => q.basic)

export const quality = (id) => QUALITIES.find((q) => q.id === id) ?? QUALITIES[0]

/**
 * What the chord would be if you just harmonised the scale. Used to preselect
 * a sensible quality when a degree is tapped, so placing a 6 gives you 6m
 * without a second decision.
 */
const DIATONIC = ['maj', 'm', 'm', 'maj', 'maj', 'm', 'dim']
export const diatonicQuality = (degree) => DIATONIC[(degree - 1) % 7]

/**
 * In a numbers chart, major and minor are not written — they're assumed from
 * the degree. Everyone reading "1 4 5 6" plays major, major, major, minor,
 * because that is what those degrees are in a major key. Writing "6m" is
 * spelling out something the number already said.
 *
 * So the suffix is only set when the chord DEPARTS from what the degree
 * implies: a major 2 is "2maj" because a 2 is otherwise minor, and a minor 4 is
 * "4m". An altered root (b3, b6, b7) is major by the same convention, so a
 * plain "b7" needs no suffix either.
 *
 * Only maj and m are suppressed. A diminished 7 stays "7°" — it is rare enough
 * that leaving it implied would surprise a player rather than help them, which
 * is the opposite of what a chart is for.
 */
export function isAssumedQuality(ch) {
  if (ch.q !== 'maj' && ch.q !== 'm') return false
  const expected = ch.a !== 0 ? 'maj' : diatonicQuality(ch.d)
  return ch.q === expected
}

/* ---------------------------------------------------------------- naming */

const sharpen = (note) =>
  note.endsWith('b') ? note.slice(0, -1) : `${note}#`
const flatten = (note) =>
  note.endsWith('#') ? note.slice(0, -1) : `${note}b`

/** The letter name of a degree in a key, respelled for any accidental. */
export function noteName(degree, accidental, key) {
  const notes = KEY_NOTES[normalizeKey(key) ?? 'C']
  let n = notes[(((degree - 1) % 7) + 7) % 7]
  if (accidental > 0) n = sharpen(n)
  if (accidental < 0) n = flatten(n)
  return n
}

const accidentalMark = (a) => (a > 0 ? '#' : a < 0 ? 'b' : '')

/**
 * One chord, split into the pieces a renderer needs to set it properly.
 *
 * The split exists because of a real ambiguity in the numbers view: a 5 chord
 * with a 9th concatenates to "59", which reads as fifty-nine, and a 5 with a
 * dominant 7th reads as fifty-seven. Nashville charts solve this by
 * superscripting the extension — 5⁹, 5⁷ — so the suffix has to be a separate
 * piece the renderer can raise. Letter charts have no such problem ("D9" cannot
 * be misread) and set it inline.
 *
 * `mode` is 'numbers' or 'chords'. Both read the same stored chord — this is a
 * rendering choice, never a data migration.
 */
export function chordParts(ch, { mode = 'chords', key = 'C' } = {}) {
  if (!ch) return { root: '', suffix: '', bass: null }
  const q = quality(ch.q)
  const root = mode === 'numbers'
    ? `${accidentalMark(ch.a)}${ch.d}`
    : noteName(ch.d, ch.a, key)
  // In numbers, a quality the degree already implies is left unwritten. When it
  // departs it must be spelled — and a major chord where a minor was expected
  // has no glyph of its own (its Nashville form is the empty suffix), so it
  // needs the explicit "maj" or it would silently render as the minor it isn't.
  const suffix = mode === 'numbers'
    ? (isAssumedQuality(ch) ? '' : (q.id === 'maj' ? 'maj' : q.n))
    : q.c

  let bass = null
  if (ch.b != null) {
    const b = mode === 'numbers'
      ? `${accidentalMark(ch.ba ?? 0)}${ch.b}`
      : noteName(ch.b, ch.ba ?? 0, key)
    // A slash chord whose bass is already the root is just the chord.
    if (b !== root) bass = b
  }
  return { root, suffix, bass }
}

/** The same chord as plain text — for chips, aria-labels and comparisons. */
export function chordText(ch, opts = {}) {
  const { root, suffix, bass } = chordParts(ch, opts)
  if (!root) return ''
  return `${root}${suffix}${bass ? `/${bass}` : ''}`
}

/** Spoken name, for the picker's screen-reader label and its caption. */
export function chordLabel(ch, key) {
  const letters = chordText(ch, { mode: 'chords', key })
  return `${letters} — ${quality(ch.q).label}${ch.b != null ? ', over the bass' : ''}`
}

/* --------------------------------------------------------------- anchors */

/**
 * Lyrics are stored as plain text and chords point into them by line and word.
 * This splits a section the same way the editor and the chart both need, so a
 * tap in one lands where the other draws.
 *
 * Empty lines are preserved — they're stanza breaks, and dropping them would
 * shift every anchor below them.
 */
export function lyricLines(lyrics = '') {
  return String(lyrics).split('\n').map((line) => ({
    text: line,
    words: line.split(/(\s+)/).filter((t) => t.trim().length > 0),
  }))
}

/** Chords on one line, in playing order. */
export const chordsOnLine = (chords, lineIndex) =>
  (chords ?? [])
    .filter((c) => c.l === lineIndex)
    .sort((a, b) => a.w - b.w)

export const chordAt = (chords, l, w) =>
  (chords ?? []).find((c) => c.l === l && c.w === w) ?? null

/** Add or replace the chord anchored at a word. */
export function putChord(chords, ch) {
  const rest = (chords ?? []).filter((c) => !(c.l === ch.l && c.w === ch.w))
  return [...rest, ch].sort((a, b) => a.l - b.l || a.w - b.w)
}

export function removeChord(chords, l, w) {
  return (chords ?? []).filter((c) => !(c.l === l && c.w === w))
}

/**
 * Every distinct chord in a song, in first-appearance order — the little
 * summary a player reads before starting.
 */
export function chordInventory(sections, { mode, key }) {
  const seen = new Set()
  for (const s of sections ?? []) {
    const ordered = [...(s.chords ?? [])].sort((a, b) => a.l - b.l || a.w - b.w)
    for (const c of ordered) {
      const t = chordText(c, { mode, key })
      if (t) seen.add(t)
    }
  }
  return [...seen]
}

/**
 * The song as ChordPro — `[G]Amazing [C]grace`, section labels on their own
 * line. This is the format Planning Center's chord-chart editor understands,
 * and the same shape a musician would paste anywhere else.
 *
 * Rendered here rather than in the edge function on purpose: chords are stored
 * as scale degrees and only this module knows how to turn a degree into a
 * letter in a given key. A second copy in Deno would drift from this one the
 * first time a quality or accidental rule changed.
 */
export function toChordPro(sections = [], { key = 'C', label } = {}) {
  const name = label ?? ((s) => {
    const base = s.section_type === 'verse' ? 'Verse' : s.section_type
    return s.section_type === 'verse' ? `${base} ${s.section_number ?? 1}` : base
  })

  return sections.map((s) => {
    const chords = s.chords ?? []
    const body = lyricLines(s.lyrics).map((line, li) => {
      if (line.words.length === 0) return ''
      // Walk the original text so spacing between words survives; chords are
      // anchored by word index, which counts only non-whitespace tokens.
      let wi = -1
      return line.text.split(/(\s+)/).map((tok) => {
        if (!tok.trim()) return tok
        wi += 1
        const ch = chordAt(chords, li, wi)
        const text = ch ? chordText(ch, { mode: 'chords', key }) : ''
        return text ? `[${text}]${tok}` : tok
      }).join('')
    }).join('\n')

    return `${name(s)}\n${body}`.trim()
  }).filter(Boolean).join('\n\n')
}
